/**
 * This is a demonstration of exporting a federated entry that provides its own routes.
 * The behavior of this component would be based upon its mount point in the parent application.
 * So, for example, if in zendash this were mounted as a route pointing at `/d/company/:uuid/x/*`, then
 * this will support pathing to any matched url starting with `/d/company/<UUID>/x/`, and all other urls will show
 * a route not found error page.
 */

import { useResolvedPath, useRoutes } from 'react-router-dom';

import { BusinessEntityGate } from '@zenbusiness/application-commons-business-entity';
import { EnvProvider } from '@zenbusiness/application-commons-env';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';

import Authenticated from '#client/App/Authenticated';
import PostRouting from '#client/App/PostRouting';
import AssistantLayout from '#client/components/AssistantLayout';
import * as Pages from '#client/pages';

import FederatedEntry from '../FederatedEntry';

const routes = [
  {
    /**
     * Assistant Routes - Shared Layout Parent
     * Groups chat, thread/:uuid, and company/:businessEntityUuid
     * under a single <AssistantLayout> instance to prevent double header flash during navigation
     */
    element: (
      <Authenticated optional>
        <AssistantLayout>
          <PostRouting />
        </AssistantLayout>
      </Authenticated>
    ),
    children: [
      {
        /**
         * Default Chat Entry Route - /guide/chat
         * Creates new thread for any user mode (anonymous, starter, business)
         * This is the default route when using Velo (assistant-ui) as MFE
         */
        path: 'chat',
        element: <Pages.UniversalThreadCreator autoCreate={false} />
      },
      {
        /**
         * Thread Continuation Route
         * thread/:threadUuid - Continues existing thread
         */
        path: 'thread/:threadUuid',
        element: <Pages.ThreadViewer />
      },
      {
        /**
         * Legacy Thread Creation Route
         * company/:businessEntityUuid - Auto-creates thread (business mode only)
         * Note: Uses stricter authentication via nested wrapper
         */
        path: 'company/:businessEntityUuid',
        element: (
          <Authenticated>
            <BusinessEntityGate loadingElement={<PageLoading />}>
              <Pages.UniversalThreadCreator autoCreate />
            </BusinessEntityGate>
          </Authenticated>
        )
      }
    ]
  },
  {
    path: '*',
    element: <PageError>The requested route could not be found</PageError>
  }
];

export default function RoutedFederatedAppEntry() {
  // ROOT_SEGMENT is used to identify where the router is mounted at.
  // At this component level, the resolved path is where the federated
  // component is mounted at in the parent application. We can use this
  // to identify the relative path that any local routes utilize.
  const ROOT_SEGMENT = useResolvedPath('.').pathname;

  const router = useRoutes(routes);
  return (
    <FederatedEntry>
      <EnvProvider env={{ ROOT_SEGMENT }}>{router}</EnvProvider>
    </FederatedEntry>
  );
}
